<template>
	<page-container :page="page">
		<heading-image
			v-if="page.headingImage"
			:heading-image="{
				image: page.headingImage,
				imageWebp: page.headingImageWebp,
				imagePortrait: page.headingImagePortrait,
				imagePortraitWebp: page.headingImagePortraitWebp,
			}"
		/>

		<section class="background-light">
			<div class="row">
				<div class="columns column12 align-center">
					<h1>Vondel Magazine</h1>
				</div>
				<div class="horizontal-divider large" />
			</div>
			<main-content
				class="wide"
				:title="page.title || page.header"
				:content="page.content"
				:intro-image="{ image: page.introductionImage, imageWebp: page.introductionImageWebp }"
			>
				<template #main-content>
					<div class="columns column12">
						<nuxt-link class="back-to-overview" :to="localePath('magazine')">
							{{ $t('back') }}
						</nuxt-link>
					</div>
					<div class="columns column6">
						<picture>
							<source :srcset="page.introImageWebp" type="image/webp" />
							<source :srcset="page.introImage" />
							<img :src="page.introImage" :alt="page.introImageAlt" loading="lazy" />
						</picture>
					</div>
					<div class="columns column6 align-left">
						<div class="content">
							<h2>{{ page.title || page.header }}</h2>
							<div v-parse-links v-html="page.content" />
						</div>
					</div>
				</template>
			</main-content>

			<section class="main-content extra-content">
				<div v-if="page.contentExtra" class="row">
					<div class="columns column12 align-left">
						<div v-parse-links v-html="page.contentExtra" />
					</div>
				</div>
				<div v-for="item in page.extraContentSection" :key="item.ID" class="row">
					<div v-if="item.sideImage">
						<div v-if="item.content" class="columns column6">
							<div v-parse-links v-html="item.content" />
						</div>
						<div class="columns column6">
							<picture>
								<source :srcset="item.sideImageWebp" type="image/webp" />
								<source :srcset="item.sideImage" />
								<img :src="item.sideImage" :alt="item.sideImageAlt" loading="lazy" />
							</picture>
						</div>
					</div>
					<div v-else-if="item.fullWidthImage">
						<div class="columns column12">
							<picture>
								<source :srcset="item.fullWidthImageWebp" type="image/webp" />
								<source :srcset="item.fullWidthImage" />
								<img :src="item.fullWidthImage" :alt="item.fullWidthImageAlt" loading="lazy" />
							</picture>
						</div>
						<div v-if="item.content" class="columns column12">
							<div v-parse-links v-html="item.content" />
						</div>
					</div>
					<div v-else>
						<div v-if="item.content" class="columns column12">
							<div v-parse-links v-html="item.content" />
						</div>
					</div>
				</div>
				<div class="row">
					<div class="columns column12">
						<nuxt-link v-if="other" class="button next-article" :to="localePath(`/${other.filename}`)">
							{{ $t('nextArticle') }}
						</nuxt-link>
					</div>
				</div>
			</section>
		</section>
	</page-container>
</template>

<script setup>
const { page, fetchPage } = usePage();
const { locale } = useI18n();
const localePath = useLocalePath();

await fetchPage();

useRemoveFormitable();

const { data: magazine } = await useWebsiteFetch('magazine', {
	query: { language: locale.value },
	key: `${locale.value}/magazine`,
});

const other = computed(
	() =>
		magazine.value.find((magazinePage) => parseInt(magazinePage.pageID, 10) > parseInt(page.value.pageID, 10)) ||
		null,
);
</script>

<style lang="scss" scoped>
.main-content > .row,
.main-content > :deep(.row) {
	max-width: 1240px;

	h2 {
		max-width: 100%;
		margin: 0 0 10px;
		color: #3a3a3c;
	}

	h3,
	h4 {
		color: #3a3a3c;
	}
}

h1 {
	max-width: 420px;
	margin: 0 auto;
	color: #3a3a3c;
}

.background-light {
	padding-bottom: 0;
}

.horizontal-divider {
	margin: 60px auto 0;
}

.main-content {
	padding: 50px 0 0;
}

.extra-content {
	padding: 50px 0 0;

	.row {
		margin: 0 auto;

		> div {
			display: flex;
			flex-flow: row wrap;
			justify-content: space-between;
			align-items: flex-start;
		}

		.columns {
			margin: 50px auto;

			&:first-child,
			&.column6 {
				margin-top: 0;
			}
		}
	}
}

.back-to-overview {
	text-decoration: none;
	text-transform: uppercase;
	color: var(--body-color);
	margin: 10px 0 40px;
	padding: 3px 0 3px 30px;
	position: relative;
	display: inline-block;

	&::before {
		width: 16px;
		height: 16px;
		content: '';
		display: block;
		position: absolute;
		top: 8px;
		left: 0;
		background: url('~/assets/images/icon-arrow-left.png') no-repeat center center;
		background-size: 16px;
	}
}

.next-article {
	margin-top: 20px;
	margin-bottom: 50px;
	margin-left: auto;
}

@media (max-width: 1080px) {
	.main-content .content {
		margin: 50px 0 0;
	}
}
</style>
